import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { helpdeskAPI } from '../services/api';
import ScreenCapture from '../components/ScreenCapture';
import './TicketDetail.css';

function TicketDetail() {
    const { id } = useParams();
    const location = useLocation();
    const { client } = useAuth();
    const { t, language } = useLanguage();
    const fileInputRef = useRef(null);

    const [ticket, setTicket] = useState(null);
    const [notes, setNotes] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [replyLoading, setReplyLoading] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyAttachments, setReplyAttachments] = useState([]);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showScreenCapture, setShowScreenCapture] = useState(false);

    useEffect(() => {
        if (location.state?.success) {
            setSuccessMessage(location.state.message);
            window.history.replaceState({}, document.title);
        }
        loadTicket();
    }, [id]);

    useEffect(() => {
        if (successMessage || error) {
            const timer = setTimeout(() => {
                setSuccessMessage('');
                setError('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage, error]);

    // Handle paste event for images in reply
    useEffect(() => {
        const handlePaste = (e) => {
            // Only handle if ticket is not closed
            if (ticket?.status === 'closed') return;
            
            const items = e.clipboardData?.items;
            if (!items) return;

            for (let item of items) {
                if (item.type.startsWith('image/')) {
                    e.preventDefault();
                    const file = item.getAsFile();
                    if (file) {
                        compressAndAddImage(file);
                    }
                    break;
                }
            }
        };

        document.addEventListener('paste', handlePaste);
        return () => document.removeEventListener('paste', handlePaste);
    }, [replyAttachments.length, ticket?.status]);

    const loadTicket = async () => {
        try {
            setLoading(true);
            const response = await helpdeskAPI.getTicket(id);
            if (response.data.success) {
                setTicket(response.data.ticket);
                setNotes(response.data.notes || []);
                setAttachments(response.data.attachments || []);
            }
        } catch (err) {
            console.error('Failed to load ticket:', err);
            setError(t('failedToLoad'));
        } finally {
            setLoading(false);
        }
    };

    const handleCloseTicket = async () => {
        const confirmMsg = language === 'ar' ? 'هل أنت متأكد من إغلاق هذه التذكرة؟' : 'Are you sure you want to close this ticket?';
        if (!window.confirm(confirmMsg)) return;
        
        try {
            setActionLoading(true);
            setError('');
            const response = await helpdeskAPI.closeTicket(id);
            if (response.data.success) {
                setSuccessMessage(t('ticketClosedSuccess'));
                loadTicket();
            } else {
                setError(response.data.message || t('failedToClose'));
            }
        } catch (err) {
            setError(err.response?.data?.message || t('failedToClose'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleReopenTicket = async () => {
        try {
            setActionLoading(true);
            setError('');
            const response = await helpdeskAPI.reopenTicket(id);
            if (response.data.success) {
                setSuccessMessage(t('ticketReopenedSuccess'));
                loadTicket();
            } else {
                setError(response.data.message || t('failedToReopen'));
            }
        } catch (err) {
            setError(err.response?.data?.message || t('failedToReopen'));
        } finally {
            setActionLoading(false);
        }
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        addFilesToReply(files);
    };

    const addFilesToReply = (files) => {
        const maxSize = 4 * 1024 * 1024; // 4MB
        const validFiles = files.filter(file => {
            if (file.size > maxSize) {
                setError(language === 'ar' ? `الملف "${file.name}" يتجاوز 4 ميجابايت` : `File "${file.name}" exceeds 4MB limit`);
                return false;
            }
            return true;
        });

        if (replyAttachments.length + validFiles.length > 4) {
            setError(language === 'ar' ? 'الحد الأقصى 4 ملفات لكل رد' : 'Maximum 4 files allowed per reply');
            return;
        }

        setReplyAttachments(prev => [...prev, ...validFiles]);
        setError('');
    };

    // Compress image before adding
    const compressAndAddImage = async (file) => {
        if (replyAttachments.length >= 4) {
            setError(language === 'ar' ? 'الحد الأقصى 4 ملفات لكل رد' : 'Maximum 4 files allowed per reply');
            return;
        }

        try {
            const compressed = await compressImage(file);
            setReplyAttachments(prev => [...prev, compressed]);
            setError('');
        } catch (err) {
            console.error('Failed to compress image:', err);
            setReplyAttachments(prev => [...prev, file]);
        }
    };

    // Image compression function
    const compressImage = (file) => {
        return new Promise((resolve, reject) => {
            if (!file.type.startsWith('image/')) {
                resolve(file);
                return;
            }

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let { width, height } = img;
                    
                    const maxDim = 2560;
                    if (width > maxDim || height > maxDim) {
                        if (width > height) {
                            height = (height / width) * maxDim;
                            width = maxDim;
                        } else {
                            width = (width / height) * maxDim;
                            height = maxDim;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    canvas.toBlob(
                        (blob) => {
                            if (blob) {
                                const compressedFile = new File([blob], file.name || `screenshot-${Date.now()}.jpg`, {
                                    type: 'image/jpeg',
                                    lastModified: Date.now()
                                });
                                resolve(compressedFile);
                            } else {
                                resolve(file);
                            }
                        },
                        'image/jpeg',
                        0.92
                    );
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = e.target.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    // Handle screenshots from ScreenCapture component
    const handleScreenCapture = (screenshotFiles) => {
        const remaining = 4 - replyAttachments.length;
        if (remaining <= 0) {
            setError(language === 'ar' ? 'الحد الأقصى 4 ملفات لكل رد' : 'Maximum 4 files allowed per reply');
            setShowScreenCapture(false);
            return;
        }

        const filesToAdd = screenshotFiles.slice(0, remaining);
        setReplyAttachments(prev => [...prev, ...filesToAdd]);
        setShowScreenCapture(false);
    };

    const removeReplyAttachment = (index) => {
        setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmitReply = async (e) => {
        e.preventDefault();
        setError('');

        if (!replyMessage.trim()) {
            setError(t('enterMessage'));
            return;
        }

        try {
            setReplyLoading(true);
            const response = await helpdeskAPI.addReply(id, {
                message: replyMessage,
                attachments: replyAttachments
            });

            if (response.data.success) {
                setReplyMessage('');
                setReplyAttachments([]);
                setSuccessMessage(t('replySentSuccess'));
                loadTicket();
            }
        } catch (err) {
            setError(err.response?.data?.message || t('failedToSendReply'));
        } finally {
            setReplyLoading(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString(language === 'ar' ? 'ar-EG' : 'en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getAttachmentUrl = (attachment) => {
        const filename = attachment.file_path.split('/').pop();
        return `https://agents.matix.one/uploads/helpdesk/${filename}`;
    };

    const getAttachmentsForNote = (noteId) => {
        return attachments.filter(att => att.note_id === noteId);
    };

    const getTicketAttachments = () => {
        return attachments.filter(att => !att.note_id);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'open': { label: t('statusOpen'), className: 'open' },
            'assigned': { label: t('statusAssigned'), className: 'assigned' },
            'in_progress': { label: t('statusInProgress'), className: 'in-progress' },
            'waiting_for_customer': { label: t('statusWaiting'), className: 'awaiting' },
            'resolved': { label: t('statusResolved'), className: 'resolved' },
            'closed': { label: t('statusClosed'), className: 'closed' }
        };
        const s = statusMap[status] || { label: status, className: 'default' };
        return <span className={`status-badge ${s.className}`}>{s.label}</span>;
    };

    const getPriorityBadge = (priority) => {
        const priorityMap = {
            'low': { label: t('priorityLow'), className: 'low' },
            'medium': { label: t('priorityMedium'), className: 'medium' },
            'high': { label: t('priorityHigh'), className: 'high' },
            'critical': { label: t('priorityCritical'), className: 'critical' }
        };
        const p = priorityMap[priority] || { label: priority, className: 'default' };
        return <span className={`priority-badge ${p.className}`}>{p.label}</span>;
    };

    // Check if file is an image
    const isImageFile = (file) => {
        return file.type?.startsWith('image/') || file.name?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
    };

    // Get image preview URL
    const getPreviewUrl = (file) => {
        if (file instanceof File && isImageFile(file)) {
            return URL.createObjectURL(file);
        }
        return null;
    };

    if (loading) {
        return <div className="loading">{t('loading')}</div>;
    }

    if (!ticket) {
        return <div className="error-page">{language === 'ar' ? 'التذكرة غير موجودة' : 'Ticket not found'}</div>;
    }

    return (
        <div className="ticket-detail-page">
            <div className="ticket-header">
                <Link to="/tickets" className="back-link">← {t('backToTickets')}</Link>
                <div className="ticket-title-row">
                    <div>
                        <h1>#{ticket.ticket_number}</h1>
                        <h2>{ticket.subject}</h2>
                    </div>
                    <div className="ticket-actions">
                        {ticket.status !== 'closed' ? (
                            <button 
                                onClick={handleCloseTicket} 
                                className="btn btn-close"
                                disabled={actionLoading}
                            >
                                {actionLoading ? '...' : '✕'} {t('closeTicket')}
                            </button>
                        ) : (
                            <button 
                                onClick={handleReopenTicket} 
                                className="btn btn-reopen"
                                disabled={actionLoading}
                            >
                                {actionLoading ? '...' : '↻'} {t('reopenTicket')}
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {error && <div className="alert alert-error">{error}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            <div className="ticket-content">
                <div className="ticket-info-card">
                    <div className="info-row">
                        <span className="info-label">{t('status')}</span>
                        {getStatusBadge(ticket.status)}
                    </div>
                    <div className="info-row">
                        <span className="info-label">{t('priority')}</span>
                        {getPriorityBadge(ticket.priority)}
                    </div>
                   <div className="info-row">
                        <span className="info-label">{t('category')}</span>
                        <span>{ticket.category_name || (language === 'ar' ? 'عام' : 'General')}</span>
                    </div>
                    {ticket.company_name && (
                        <div className="info-row">
                            <span className="info-label">{language === 'ar' ? 'الشركة' : 'Company'}</span>
                            <span>🏢 {ticket.company_name}</span>
                        </div>
                    )}
                    <div className="info-row">
                        <span className="info-label">{t('created')}</span>
                        <span>{formatDate(ticket.created_at)}</span>
                    </div>
                    <div className="info-row">
                        <span className="info-label">{t('lastUpdated')}</span>
                        <span>{formatDate(ticket.updated_at)}</span>
                    </div>
                    
                    {attachments.length > 0 && (
                        <div className="info-row attachments-summary">
                            <span className="info-label">📎 {language === 'ar' ? 'المرفقات' : 'Attachments'}</span>
                            <span>{attachments.length} {language === 'ar' ? 'ملف' : 'file(s)'}</span>
                        </div>
                    )}
                </div>

                <div className="conversation-section">
                    <h3>{t('conversation')}</h3>
                    
                    {/* Original Ticket Message */}
                    <div className="message message-client">
                        <div className="message-header">
                            <span className="message-author">{ticket.client_name} ({language === 'ar' ? 'أنت' : 'You'})</span>
                            <span className="message-date">{formatDate(ticket.created_at)}</span>
                        </div>
                        <div className="message-body">{ticket.description}</div>
                        
                        {getTicketAttachments().length > 0 && (
                            <div className="message-attachments">
                                <strong>📎 {language === 'ar' ? 'المرفقات:' : 'Attachments:'}</strong>
                                <div className="attachments-grid">
                                    {getTicketAttachments().map(att => (
                                        <a 
                                            key={att.id} 
                                            href={getAttachmentUrl(att)} 
                                            target="_blank" 
                                            rel="noopener noreferrer"
                                            className={`attachment-link ${att.file_type?.startsWith('image/') ? 'image-attachment' : ''}`}
                                        >
                                            {att.file_type?.startsWith('image/') ? (
                                                <img src={getAttachmentUrl(att)} alt={att.file_name} />
                                            ) : (
                                                <span>📄 {att.file_name}</span>
                                            )}
                                            <span className="file-size">({formatFileSize(att.file_size)})</span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Notes/Replies */}
                    {notes.map(note => (
                        <div 
                            key={note.id} 
                            className={`message ${note.author_type === 'client' ? 'message-client' : 'message-agent'}`}
                        >
                            <div className="message-header">
                                <span className="message-author">
                                    {note.author_type === 'client' 
                                        ? `${note.author_name} (${language === 'ar' ? 'أنت' : 'You'})` 
                                        : note.author_name || (language === 'ar' ? 'فريق الدعم' : 'Support Agent')}
                                </span>
                                <span className="message-date">{formatDate(note.created_at)}</span>
                            </div>
                            <div className="message-body">{note.message}</div>
                            
                            {getAttachmentsForNote(note.id).length > 0 && (
                                <div className="message-attachments">
                                    <strong>📎 {language === 'ar' ? 'المرفقات:' : 'Attachments:'}</strong>
                                    <div className="attachments-grid">
                                        {getAttachmentsForNote(note.id).map(att => (
                                            <a 
                                                key={att.id} 
                                                href={getAttachmentUrl(att)} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className={`attachment-link ${att.file_type?.startsWith('image/') ? 'image-attachment' : ''}`}
                                            >
                                                {att.file_type?.startsWith('image/') ? (
                                                    <img src={getAttachmentUrl(att)} alt={att.file_name} />
                                                ) : (
                                                    <span>📄 {att.file_name}</span>
                                                )}
                                                <span className="file-size">({formatFileSize(att.file_size)})</span>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Reply Form */}
                    {ticket.status !== 'closed' ? (
                        <form onSubmit={handleSubmitReply} className="reply-form">
                            <h4>{t('addReply')}</h4>
                            <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder={t('typeMessage')}
                                rows={4}
                            />
                            
                            <div className="reply-actions">
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    multiple
                                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                                    style={{ display: 'none' }}
                                />
                                <button 
                                    type="button" 
                                    onClick={() => fileInputRef.current?.click()} 
                                    className="btn btn-secondary"
                                >
                                    📎 {t('attachFiles')}
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => setShowScreenCapture(true)} 
                                    className="btn btn-screenshot"
                                >
                                    📷 {language === 'ar' ? 'صورة شاشة' : 'Screenshot'}
                                </button>
                                <button type="submit" className="btn btn-primary" disabled={replyLoading}>
                                    {replyLoading ? '...' : t('sendReply')}
                                </button>
                            </div>

                            <p className="paste-hint">
                                {language === 'ar' ? '💡 يمكنك لصق صورة مباشرة (Ctrl+V)' : '💡 You can paste an image directly (Ctrl+V)'}
                            </p>
                            
                            {replyAttachments.length > 0 && (
                                <div className="reply-attachments">
                                    {replyAttachments.map((file, index) => {
                                        const previewUrl = getPreviewUrl(file);
                                        return (
                                            <div key={index} className="attachment-item">
                                                {previewUrl ? (
                                                    <div className="attachment-preview">
                                                        <img src={previewUrl} alt={file.name} />
                                                    </div>
                                                ) : (
                                                    <span className="attachment-icon">📄</span>
                                                )}
                                                <div className="attachment-info">
                                                    <span className="attachment-name">{file.name}</span>
                                                    <span className="attachment-size">{formatFileSize(file.size)}</span>
                                                </div>
                                                <button type="button" onClick={() => removeReplyAttachment(index)} className="attachment-remove">×</button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </form>
                    ) : (
                        <div className="ticket-closed-notice">
                            <p>{t('ticketClosed')}</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Screen Capture Modal */}
            {showScreenCapture && (
                <ScreenCapture
                    onCapture={handleScreenCapture}
                    onClose={() => setShowScreenCapture(false)}
                    language={language}
                    maxImages={4 - replyAttachments.length}
                />
            )}
        </div>
    );
}

export default TicketDetail;
